import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Tag, InputNumber, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getTenantPageList, getTenantDetail, addTenant, updateTenant, deleteTenant, updateTenantStatus } from '../../api/tenant';
import './index.css';
import dayjs from 'dayjs';
import AvatarUpload from '../../components/Upload';

const { Option } = Select as any;
const { TextArea } = Input;

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [searchParams, setSearchParams] = useState({
    tenantId: '',
    tenantName: '',
    domain: '',
    status: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [form] = Form.useForm();

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '租户ID',
      dataIndex: 'tenantId',
      key: 'tenantId',
      width: 150,
    },
    {
      title: '租户名称',
      dataIndex: 'tenantName',
      key: 'tenantName',
      width: 150,
    },
    {
      title: '域名',
      dataIndex: 'domain',
      key: 'domain',
      width: 150,
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 120,
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 130,
    },
    {
      title: '联系邮箱',
      dataIndex: 'contactEmail',
      key: 'contactEmail',
      width: 180,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          0: { text: '禁用', color: 'red' },
          1: { text: '启用', color: 'green' },
        };
        const statusInfo = statusMap[status] || { text: '未知', color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '过期时间',
      dataIndex: 'expireTime',
      key: 'expireTime',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '最大用户数',
      dataIndex: 'maxUserCount',
      key: 'maxUserCount',
      width: 120,
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => handleTogglestatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确定要删除这个租户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 获取租户列表
  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = {
        PageIndex: pagination.current,
        PageSize: pagination.pageSize,
        ...searchParams,
      };
      
      const response: any = await getTenantPageList(params);
      
      if (response?.res_status_code === '0' && response?.res_content) {
        setTenants(response.res_content.items || []);
        const rawTotal =
          response?.res_content?.totalCount ??
          response?.res_content?.TotalCount ??
          response?.totalCount ??
          response?.TotalCount ??
          response?.res_content?.total ??
          response?.res_content?.Total ??
          0;
        const total = Number(rawTotal) || 0;
        setPagination((prev) => ({ ...prev, total }));
      } else {
        message.error(response?.res_message || '获取租户列表失败');
      }
    } catch (error) {
      console.error('获取租户列表失败:', error);
      message.error('获取租户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [pagination.current, pagination.pageSize]);

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchTenants();
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({
      tenantId: '',
      tenantName: '',
      domain: '',
      status: null,
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => {
      fetchTenants();
    }, 100);
  };

  // 新增租户
  const handleAdd = () => {
    setEditingTenant(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑租户
  const handleEdit = async (record) => {
    try {
      const response: any = await getTenantDetail(record.id);
      if (response?.res_status_code === '0' && response?.res_content) {
        const tenant = response.res_content;
        setEditingTenant(tenant);
        form.setFieldsValue({
          ...tenant,
          expireTime: tenant.expireTime ? dayjs(tenant.expireTime) : null,
        });
        setModalVisible(true);
      } else {
        message.error(response?.res_message || '获取租户详情失败');
      }
    } catch (error) {
      console.error('获取租户详情失败:', error);
      message.error('获取租户详情失败');
    }
  };

  // 保存租户
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        expireTime: values.expireTime ? values.expireTime.format('YYYY-MM-DD HH:mm:ss') : null,
      };

      if (editingTenant) {
        // 更新
        data.Id = editingTenant.id;
        const response: any = await updateTenant(data);
        if (response?.res_status_code === '0') {
          message.success('更新租户成功');
          setModalVisible(false);
          fetchTenants();
        } else {
          message.error(response?.res_message || '更新租户失败');
        }
      } else {
        // 新增
        const response: any = await addTenant(data);
        if (response?.res_status_code === '0') {
          message.success('添加租户成功');
          setModalVisible(false);
          fetchTenants();
        } else {
          message.error(response?.res_message || '添加租户失败');
        }
      }
    } catch (error) {
      console.error('保存租户失败:', error);
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error('保存租户失败');
    }
  };

  // 删除租户
  const handleDelete = async (id) => {
    try {
      const response: any = await deleteTenant({ Ids: [id] });
      if (response?.res_status_code === '0') {
        message.success('删除租户成功');
        fetchTenants();
      } else {
        message.error(response?.res_message || '删除租户失败');
      }
    } catch (error) {
      console.error('删除租户失败:', error);
      message.error('删除租户失败');
    }
  };

  // 切换状态
  const handleTogglestatus = async (record) => {
    try {
      const newstatus = record.status === 1 ? 0 : 1;
      const response: any = await updateTenantStatus({
        Status: newstatus,
        Ids: [record.id],
      });
      if (response?.res_status_code === '0') {
        message.success(`${newstatus === 1 ? '启用' : '禁用'}租户成功`);
        fetchTenants();
      } else {
        message.error(response?.res_message || '更新状态失败');
      }
    } catch (error) {
      console.error('更新状态失败:', error);
      message.error('更新状态失败');
    }
  };

  // 分页变化
  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current:
        newPagination.pageSize && newPagination.pageSize !== prev.pageSize
          ? 1
          : newPagination.current ?? prev.current,
      pageSize: newPagination.pageSize ?? prev.pageSize,
    }));
  };

  return (
    <div className="tenants-page">
      <div className="page-header">
        <h2>租户管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增租户
        </Button>
      </div>

      {/* 搜索区域 */}
      <div className="search-container">
        <Space size="middle" wrap>
          <Input
            placeholder="租户ID"
            value={searchParams.tenantId}
            onChange={(e) => setSearchParams({ ...searchParams, tenantId: e.target.value })}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="租户名称"
            value={searchParams.tenantName}
            onChange={(e) => setSearchParams({ ...searchParams, tenantName: e.target.value })}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="域名"
            value={searchParams.domain}
            onChange={(e) => setSearchParams({ ...searchParams, domain: e.target.value })}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="状态"
            value={searchParams.status}
            onChange={(value) => setSearchParams({ ...searchParams, status: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value={1}>启用</Option>
            <Option value={0}>禁用</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={tenants}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          size: 'small',
        }}
        onChange={handleTableChange}
        scroll={{ x: 1500, y: 'calc(100vh - 320px)' }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingTenant ? '编辑租户' : '新增租户'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="horizontal"
          labelAlign="right"
          colon={false}
          labelCol={{ flex: '110px' }}
          wrapperCol={{ flex: 1 }}
        >
          <Form.Item
            name="tenantId"
            label="租户ID"
            rules={[{ required: true, message: '请输入租户ID' }]}
          >
            <Input placeholder="请输入租户ID" />
          </Form.Item>
          <Form.Item
            name="tenantName"
            label="租户名称"
            rules={[{ required: true, message: '请输入租户名称' }]}
          >
            <Input placeholder="请输入租户名称" />
          </Form.Item>
          <Form.Item
            name="domain"
            label="域名"
          >
            <Input placeholder="请输入域名" />
          </Form.Item>
          <Form.Item
            name="logo"
            label="logo"
          >
            <AvatarUpload uploadText="上传logo" maxFiles={1} />
          </Form.Item>
          <Form.Item
            name="contactPerson"
            label="联系人"
          >
            <Input placeholder="请输入联系人" />
          </Form.Item>
          <Form.Item
            name="contactPhone"
            label="联系电话"
          >
            <Input placeholder="请输入联系电话" />
          </Form.Item>
          <Form.Item
            name="contactEmail"
            label="联系邮箱"
          >
            <Input placeholder="请输入联系邮箱" />
          </Form.Item>
          <Form.Item
            name="industry"
            label="行业"
          >
            <Input placeholder="请输入行业" />
          </Form.Item>
          <Form.Item
            name="address"
            label="地址"
          >
            <Input placeholder="请输入地址" />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
          >
            <Select>
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="expireTime"
            label="过期时间"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>
          <Form.Item
            name="maxUserCount"
            label="最大用户数"
          >
            <InputNumber style={{ width: '100%' }} min={0} placeholder="留空表示不限制" />
          </Form.Item>
          <Form.Item
            name="remark"
            label="备注"
          >
            <TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Tenants;
